// Money on the operator screens.
//
// The wire carries integer cents and states no currency and no tax, so
// neither does the screen: an amount is drawn as a number with two
// decimals. Arithmetic stays in cents; a float never holds money here.

const formatter = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Cents as an amount a person reads: `1234.5` never appears, `1,234.50` does. */
export function formatMoney(cents: number): string {
  return formatter.format(cents / 100)
}

/**
 * An amount a person typed, as cents, or `null` when it is not one.
 *
 * Parsed as text rather than through `parseFloat`, because `19.99 * 100`
 * is `1998.9999999999998` and rounding it back is how a cent goes missing.
 */
export function parseMoney(text: string): number | null {
  const match = /^\s*(\d{1,12})(?:\.(\d{1,2}))?\s*$/.exec(text.replace(/,/g, ''))
  if (!match) return null
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'))
}

/** Today in the browser's calendar, `YYYY-MM-DD`. */
export function todayISO(): string {
  return new Date().toLocaleDateString('en-CA')
}

/** A `YYYY-MM-DD` date drawn for a person. */
export function formatDate(iso: string | undefined): string {
  if (!iso) return '—'
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** A `YYYY-MM` month drawn for a person, with no day in any locale. */
export function formatMonth(iso: string): string {
  const [year, month] = iso.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}
